import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Calendar, Pill, BookOpen, Dumbbell, Salad, CheckCircle2, ExternalLink, Sparkles, Lock } from "lucide-react";

export default function ClientMTMWeek() {
  const { weekNumber } = useParams();
  const n = parseInt(weekNumber, 10);
  const [week, setWeek] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    setWeek(null); setError("");
    api.get(`/mtm/weeks/${n}`).then((r) => setWeek(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Could not load"));
  }, [n]);

  if (error) return (
    <div className="lg:mt-0 mt-10 max-w-xl" data-testid="week-locked">
      <Link to="/app/program" className="btn-ghost"><ArrowLeft size={12}/> Back</Link>
      <p className="overline mt-6">{error === "Locked" ? "Locked" : "Unavailable"}</p>
      <h1 className="font-display text-3xl mt-4 leading-tight">
        <span className="font-script normal-case" style={{ fontSize: "1.15em", color: "var(--accent)", letterSpacing: 0 }}>This week opens in time.</span>
      </h1>
      <div className="bronze-rule mt-5" />
      <p className="font-soft text-lg mt-6" style={{ color: "var(--text-secondary)" }}>
        Trust the pacing — there&apos;s a reason each week is sequenced this way.
      </p>
    </div>
  );

  if (!week) return <div className="lg:mt-0 mt-10 overline">Loading week {n}…</div>;

  // Preview-only state
  if (week.preview_only) {
    return (
      <div className="lg:mt-0 mt-10 max-w-3xl space-y-6" data-testid="week-preview">
        <Link to="/app/program" className="btn-ghost"><ArrowLeft size={12}/> Back to the method</Link>
        <p className="overline">Next week · {week.phase} phase</p>
        <h1 className="font-display text-4xl sm:text-5xl leading-tight">
          Week {week.week} — {week.title}
        </h1>
        <div className="bronze-rule" />
        <div className="p-8" style={{ background: "var(--surface-2)", border: "0.5px solid var(--border-strong)" }}>
          <div className="flex items-start gap-3">
            <Sparkles size={16} style={{ color: "var(--accent)" }} className="mt-1" />
            <div>
              <p className="overline">Prepare for next week</p>
              <p className="font-soft text-lg mt-3" style={{ color: "var(--text-primary)" }}>{week.summary}</p>
              {week.preview_next && (
                <p className="font-soft text-base mt-4 italic" style={{ color: "var(--text-secondary)" }}>{week.preview_next}</p>
              )}
            </div>
          </div>
        </div>
        <p className="font-soft text-base italic mt-6" style={{ color: "var(--text-tertiary)" }}>
          Full content unlocks the day this week begins.
        </p>
      </div>
    );
  }

  return (
    <div className="lg:mt-0 mt-10 space-y-10 max-w-4xl" data-testid="week-detail">
      <Link to="/app/program" className="btn-ghost"><ArrowLeft size={12}/> Back to the method</Link>

      <header>
        <p className="overline">{week.phase} phase · Week {week.week}</p>
        <h1 className="font-display text-4xl sm:text-5xl mt-6 leading-tight">{week.title}</h1>
        <div className="bronze-rule mt-6" />
        {week.summary && (
          <p className="font-soft text-xl mt-6 max-w-2xl" style={{ color: "var(--text-secondary)" }}>
            {week.summary}
          </p>
        )}
      </header>

      {/* Zoom call card */}
      {(week.zoom_url || week.zoom_when) && (
        <section className="p-8 md:p-10" style={{ background: "var(--ink)", color: "#FFFFFF" }} data-testid="week-zoom">
          <div className="flex items-start gap-4">
            <Calendar size={18} style={{ color: "var(--accent)" }} className="mt-1" />
            <div className="flex-1">
              <p className="overline" style={{ color: "var(--accent)" }}>Group coaching call · Zoom</p>
              <h2 className="font-display text-2xl mt-3" style={{ color: "#FFFFFF" }}>{week.zoom_when || "Time TBA"}</h2>
              {week.zoom_url && (
                <a href={week.zoom_url} target="_blank" rel="noreferrer" data-testid="zoom-join"
                  className="btn-primary mt-6 inline-flex" style={{ background: "var(--accent)", borderColor: "var(--accent)", color: "#FFFFFF" }}>
                  Join the call <ExternalLink size={12} />
                </a>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Learning content */}
      {week.learning && (
        <Section icon={BookOpen} label="The teaching">
          <div className="font-soft text-lg leading-relaxed whitespace-pre-wrap" style={{ color: "var(--text-primary)" }}>
            {week.learning}
          </div>
        </Section>
      )}

      {/* Nutrition */}
      {week.nutrition_notes && (
        <Section icon={Salad} label="Nutrition focus">
          <div className="font-soft text-lg leading-relaxed whitespace-pre-wrap" style={{ color: "var(--text-primary)" }}>
            {week.nutrition_notes}
          </div>
        </Section>
      )}

      {/* Workouts */}
      {week.workout_notes && (
        <Section icon={Dumbbell} label="Movement">
          <div className="font-soft text-lg leading-relaxed whitespace-pre-wrap" style={{ color: "var(--text-primary)" }}>
            {week.workout_notes}
          </div>
        </Section>
      )}

      {/* Habits */}
      {week.habit_prompts?.length > 0 && (
        <Section icon={CheckCircle2} label="This week&apos;s rituals">
          <ul className="space-y-3 mt-2">
            {week.habit_prompts.map((h, i) => (
              <li key={`${h.slice(0, 24)}-${i}`} className="font-soft text-lg flex items-start gap-3" style={{ color: "var(--text-primary)" }}>
                <span className="font-soft text-base" style={{ color: "var(--accent)" }}>0{i + 1}</span>
                <span>{h}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* Supplements with affiliate links */}
      {week.supplements?.length > 0 && (
        <Section icon={Pill} label="Supplements & protocols">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
            {week.supplements.map((s) => (
              <a key={s.supplement_id} href={s.affiliate_url} target="_blank" rel="noreferrer"
                data-testid={`supp-${s.supplement_id}`}
                className="block p-5 border transition-colors hover:border-accent"
                style={{ borderColor: "var(--border-strong)", background: "var(--surface-1)" }}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-display text-lg leading-tight">{s.name}</div>
                    {s.brand && <div className="font-label text-[10px] tracking-widest uppercase mt-1" style={{ color: "var(--text-tertiary)" }}>{s.brand}</div>}
                  </div>
                  <ExternalLink size={14} style={{ color: "var(--accent)" }} />
                </div>
                {s.purpose && <p className="font-soft text-sm mt-3" style={{ color: "var(--text-secondary)" }}>{s.purpose}</p>}
                {s.discount_code && (
                  <div className="mt-3 inline-block px-2 py-1 font-label text-[10px] tracking-widest uppercase"
                    style={{ background: "rgba(168,152,128,0.12)", color: "var(--accent)" }}>
                    Code: {s.discount_code}
                  </div>
                )}
              </a>
            ))}
          </div>
        </Section>
      )}

      {/* Next-week preview */}
      {week.preview_next && (
        <section className="p-8" style={{ background: "var(--surface-2)", border: "0.5px solid var(--border-strong)" }}>
          <div className="flex items-start gap-3">
            <Sparkles size={16} style={{ color: "var(--accent)" }} className="mt-1" />
            <div>
              <p className="overline">Prepare for next week</p>
              <p className="font-soft text-lg mt-3 italic" style={{ color: "var(--text-secondary)" }}>{week.preview_next}</p>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

function Section({ icon: Icon, label, children }) {
  return (
    <section>
      <div className="flex items-center gap-3 mb-4">
        <Icon size={14} style={{ color: "var(--accent)" }} />
        <p className="overline">{label}</p>
      </div>
      <div className="bronze-rule mb-6" />
      {children}
    </section>
  );
}
